// src/pages/FeatureFlagsAdmin.tsx
import { useState, useMemo } from 'react';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureStore } from '@/stores/featureStore';
import {
  getFeatureFlags,
  getExposureFlags,
  isMatchingPreset,
  CORE_PRESET,
  CORE_EXPOSURE_DEFAULTS,
  PRESET_MANAGED_FEATURE_KEYS,
  ADMIN_ONLY_FEATURE_KEYS,
  ADMIN_ONLY_EXPOSURE_KEYS,
  type PlanTier,
  type FeatureFlags,
  type ExposureFlags,
} from '@/lib/features/featureFlags';
import {
  getStoredOverrides,
  clearAllOverrides,
} from '@/hooks/useFeatureFlagOverrides';
// FF-D: `setAllEntitlementFlags` / `setAllExposureFlags` were removed
// from `useFeatureFlagOverrides` to eliminate the module-level
// nuclear escalation surface. The "Enable All / Disable All" buttons
// below now inline the equivalent loop inside this admin-gated page,
// guarded by the page's own `useIsInternalAdmin` check. See FF-D
// closure memo.
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, RotateCcw, Search, ToggleLeft, ToggleRight, Layers } from 'lucide-react';

const mapShopTierToPlanTier = (tier: string | null): PlanTier => {
  const normalized = (tier || 'CORE').toUpperCase();
  if (normalized === 'CATALYST' || normalized === 'ENTERPRISE') return normalized as PlanTier;
  if (normalized === 'DISPATCH') return 'CATALYST';
  return 'CORE';
};

export default function FeatureFlagsAdmin() {
  const { data: isAdmin, isLoading } = useIsInternalAdmin();
  const { activeShop } = useAuth();
  const { featureFlags, exposureFlags, setFeatureFlags, setExposureFlags } = useFeatureStore();
  const [search, setSearch] = useState('');
  const [showPresetConfirm, setShowPresetConfirm] = useState(false);

  const plan = mapShopTierToPlanTier(activeShop?.tier || 'CORE');
  const addons: string[] = (activeShop as any)?.addons || [];

  // Compute tier defaults (what flags would be without any overrides)
  const defaultFeatureFlags = useMemo(() => getFeatureFlags(plan, addons), [plan, addons]);
  const defaultExposureFlags = useMemo(() => getExposureFlags(plan, addons), [plan, addons]);

  const storedOverrides = getStoredOverrides();

  // Core preset matching — checks both entitlement and exposure flags
  const isCorePresetActive = useMemo(() => {
    if (!featureFlags || !exposureFlags) return false;
    return isMatchingPreset(featureFlags, exposureFlags, CORE_PRESET, CORE_EXPOSURE_DEFAULTS);
  }, [featureFlags, exposureFlags]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center p-8">
        <h1 className="text-2xl font-bold mb-2">403 — Access Denied</h1>
        <p className="text-muted-foreground">This page is restricted to internal administrators.</p>
      </div>
    );
  }

  const toggleFeatureFlag = (key: keyof FeatureFlags) => {
    if (!featureFlags) return;
    const newValue = !featureFlags[key];
    const updated = { ...featureFlags, [key]: newValue };
    setFeatureFlags(updated);

    const overrides = { ...storedOverrides.featureOverrides, [key]: newValue };
    sessionStorage.setItem('ff_overrides', JSON.stringify(overrides));
  };

  const toggleExposureFlag = (key: keyof ExposureFlags) => {
    if (!exposureFlags) return;
    const newValue = !exposureFlags[key];
    const updated = { ...exposureFlags, [key]: newValue };
    setExposureFlags(updated);

    const overrides = { ...storedOverrides.exposureOverrides, [key]: newValue };
    sessionStorage.setItem('ef_overrides', JSON.stringify(overrides));
  };

  const handleResetAll = () => {
    clearAllOverrides(plan, addons);
  };

  // FF-D: inline replacements for the removed module-level
  // `setAllEntitlementFlags` / `setAllExposureFlags` exports. These
  // are private to this admin-gated page (route is wrapped in
  // `<InternalAdminRoute>` AND the component returns early when
  // `!isAdmin`). The defensive `if (!isAdmin)` guard inside each
  // function provides defense-in-depth: if a future refactor
  // exposes these handlers via a different path, they still refuse
  // to act unless the calling render saw an admin response.
  const setAllEntitlements = (value: boolean) => {
    if (!isAdmin) return;
    if (!featureFlags) return;
    const allOverrides: Partial<FeatureFlags> = {};
    for (const key of Object.keys(featureFlags) as Array<keyof FeatureFlags>) {
      allOverrides[key] = value;
    }
    sessionStorage.setItem('ff_overrides', JSON.stringify(allOverrides));
    setFeatureFlags({ ...featureFlags, ...allOverrides });
  };

  const setAllExposures = (value: boolean) => {
    if (!isAdmin) return;
    if (!exposureFlags) return;
    const allOverrides: Partial<ExposureFlags> = {};
    for (const key of Object.keys(exposureFlags) as Array<keyof ExposureFlags>) {
      allOverrides[key] = value;
    }
    sessionStorage.setItem('ef_overrides', JSON.stringify(allOverrides));
    setExposureFlags({ ...exposureFlags, ...allOverrides });
  };

  const handleApplyCorePreset = () => {
    if (!featureFlags || !exposureFlags) return;

    // Apply CORE_PRESET to all preset-managed feature keys (excluding admin-only)
    const newFeatureOverrides: Partial<FeatureFlags> = {};
    for (const key of PRESET_MANAGED_FEATURE_KEYS) {
      if (ADMIN_ONLY_FEATURE_KEYS.includes(key)) continue;
      newFeatureOverrides[key] = CORE_PRESET[key] ?? false;
    }
    const updatedFeatures = { ...featureFlags, ...newFeatureOverrides };
    setFeatureFlags(updatedFeatures);
    sessionStorage.setItem('ff_overrides', JSON.stringify({
      ...storedOverrides.featureOverrides,
      ...newFeatureOverrides,
    }));

    // Reset non-admin exposure flags to Core defaults (all false)
    const newExposureOverrides: Partial<ExposureFlags> = {};
    const allExposureKeys = Object.keys(exposureFlags) as Array<keyof ExposureFlags>;
    for (const key of allExposureKeys) {
      if (ADMIN_ONLY_EXPOSURE_KEYS.includes(key)) continue;
      newExposureOverrides[key] = CORE_EXPOSURE_DEFAULTS[key] ?? false;
    }
    const updatedExposure = { ...exposureFlags, ...newExposureOverrides };
    setExposureFlags(updatedExposure);
    sessionStorage.setItem('ef_overrides', JSON.stringify({
      ...storedOverrides.exposureOverrides,
      ...newExposureOverrides,
    }));

    setShowPresetConfirm(false);
  };

  const featureKeys = featureFlags ? Object.keys(featureFlags) as Array<keyof FeatureFlags> : [];
  const exposureKeys = exposureFlags ? Object.keys(exposureFlags) as Array<keyof ExposureFlags> : [];

  const filterKeys = <T extends string>(keys: T[]) =>
    search ? keys.filter(k => k.toLowerCase().includes(search.toLowerCase())) : keys;

  const filteredFeatureKeys = filterKeys(featureKeys);
  const filteredExposureKeys = filterKeys(exposureKeys);

  const overrideCount =
    Object.keys(storedOverrides.featureOverrides).length +
    Object.keys(storedOverrides.exposureOverrides).length;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Feature Flags Admin</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Session-local overrides — changes survive refresh but not tab close.
          {' '}Tier: <Badge variant="outline" className="ml-1">{plan}</Badge>
          {overrideCount > 0 && (
            <Badge variant="destructive" className="ml-2">{overrideCount} override{overrideCount !== 1 ? 's' : ''}</Badge>
          )}
        </p>
      </div>

      {/* Core Preset Toggle */}
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Layers className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Core Preset</p>
              <p className="text-xs text-muted-foreground">
                {isCorePresetActive
                  ? 'All flags match the locked Core configuration.'
                  : 'Flags differ from the Core preset. Toggle ON to apply.'}
              </p>
            </div>
          </div>
          <Switch
            checked={isCorePresetActive}
            onCheckedChange={(checked) => {
              if (checked) setShowPresetConfirm(true);
              // OFF is a no-op — toggle just reflects state
            }}
          />
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={handleResetAll}>
          <RotateCcw className="h-4 w-4 mr-1" />
          Reset All to Defaults
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAllEntitlements(true)}>
          <ToggleRight className="h-4 w-4 mr-1" />
          Enable All Entitlement
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAllEntitlements(false)}>
          <ToggleLeft className="h-4 w-4 mr-1" />
          Disable All Entitlement
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAllExposures(true)}>
          <ToggleRight className="h-4 w-4 mr-1" />
          Enable All Exposure
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAllExposures(false)}>
          <ToggleLeft className="h-4 w-4 mr-1" />
          Disable All Exposure
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search flags..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Entitlement Flags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Entitlement Flags ({filteredFeatureKeys.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredFeatureKeys.map((key) => {
              const current = featureFlags?.[key] ?? false;
              const defaultVal = defaultFeatureFlags[key];
              const isOverridden = key in storedOverrides.featureOverrides;
              return (
                <FlagRow
                  key={key}
                  flagKey={key}
                  current={current}
                  defaultValue={defaultVal}
                  isOverridden={isOverridden}
                  onToggle={() => toggleFeatureFlag(key)}
                />
              );
            })}
            {filteredFeatureKeys.length === 0 && (
              <p className="text-sm text-muted-foreground p-4">No matching flags.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Exposure Flags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Exposure Flags ({filteredExposureKeys.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredExposureKeys.map((key) => {
              const current = exposureFlags?.[key] ?? false;
              const defaultVal = defaultExposureFlags[key];
              const isOverridden = key in storedOverrides.exposureOverrides;
              return (
                <FlagRow
                  key={key}
                  flagKey={key}
                  current={current}
                  defaultValue={defaultVal}
                  isOverridden={isOverridden}
                  onToggle={() => toggleExposureFlag(key)}
                />
              );
            })}
            {filteredExposureKeys.length === 0 && (
              <p className="text-sm text-muted-foreground p-4">No matching flags.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Core Preset Confirmation Dialog */}
      <AlertDialog open={showPresetConfirm} onOpenChange={setShowPresetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply Core Preset?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all preset-managed flags to the Core configuration.
              Custom changes will be overwritten. Admin-only flags will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApplyCorePreset}>Apply Core</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FlagRow({
  flagKey,
  current,
  defaultValue,
  isOverridden,
  onToggle,
}: {
  flagKey: string;
  current: boolean;
  defaultValue: boolean;
  isOverridden: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <code className="text-sm font-mono truncate">{flagKey}</code>
        {isOverridden && (
          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 shrink-0">
            overridden
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <span className="text-xs text-muted-foreground w-16 text-right">
          default: {defaultValue ? '✓' : '✗'}
        </span>
        <Switch checked={current} onCheckedChange={onToggle} />
      </div>
    </div>
  );
}
